import React, { useCallback, useMemo, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useParams } from "react-router";
import { convertScanCheckLabel } from "@sscs/report/utils";
import {
  Button,
  CheckLabel,
  HighlightMatch,
  Link,
  TreeNode,
  TreeView,
} from "@sscs/ui";
import { AddIcon } from "@sscs/ui/icons";
import { AllowedTo } from "Components";
import { PACKAGE_ROUTE, PERMISSION, PROJECT_ROUTE } from "Constants";
import {
  useAllowedTo,
  useCurrentOrganization,
  useDialog,
  useProjectsRoute,
} from "Hooks";
import { useGetBaseOrganizationUsageQuery } from "Services";
import { PackageSummary, ProjectSummary } from "Types";
import ConfigureImport from "../../../components/ConfigureImport/ConfigureImport";
import CreatePackage from "../../../components/CreatePackage/CreatePackage";
import DeletePackageDialog from "../../../components/DeletePackageDialog/DeletePackageDialog";
import DetachFromCatalog from "../../../components/DetachFromCatalog/DetachFromCatalog";
import { PROJECTS_NAVIGATION, PROJECT_MENU } from "../../../constants";
import styles from "./navigationSidebar.module.scss";
import PackageMenu from "./PackageMenu";
import ProjectMenu from "./ProjectMenu";

type NavigationSidebarProps = {
  projects: ProjectSummary[] | undefined;
  filterValue: string;
  onResetSearch: () => void;
};

const NavigationSidebar = ({
  projects = [],
  filterValue,
  onResetSearch,
}: NavigationSidebarProps): React.JSX.Element | null => {
  const currentOrganization = useCurrentOrganization();
  const { data: licensing = { max_packages_per_organization: 0 } } =
    useGetBaseOrganizationUsageQuery(
      currentOrganization
        ? { organizationId: currentOrganization.id }
        : skipToken,
    );
  const [selectedPackageInfo, setSelectedPackageInfo] = useState<
    PackageSummary | undefined
  >(undefined);
  const [selectedProject, setSelectedProject] = useState<
    { id: string; name: string } | undefined
  >(undefined);

  const canCrudPackage = useAllowedTo({
    permissions: [PERMISSION.CREATE_PACKAGE],
  });

  const { packageId: packageIdUrlParam, projectId: projectIdUrlParam } =
    useParams();

  const projectsRoute = useProjectsRoute();

  const {
    isOpen: isEditDialogOpen,
    open: openEditDialog,
    close: closeEditDialog,
  } = useDialog();
  const {
    isOpen: isDeleteDialogOpen,
    open: openDeleteDialog,
    close: closeDeleteDialog,
  } = useDialog();
  const configureImportDialog = useDialog();
  const detachDialog = useDialog();

  const handleOpenEditDialog = useCallback(
    (projectId: string, projectName: string, packageInfo?: PackageSummary) => {
      setSelectedProject({ id: projectId, name: projectName });
      setSelectedPackageInfo(packageInfo);
      openEditDialog();
    },
    [openEditDialog],
  );

  const maxPackagesLimit = licensing.max_packages_per_organization;

  const selected = packageIdUrlParam ?? projectIdUrlParam ?? null;

  const nodes = useMemo<TreeNode[]>(
    () =>
      projects.map((project) => {
        const packageNodes: TreeNode[] = project.packages.map((pkg) => {
          const { status, scanLevel, isRepro, isDiff } = convertScanCheckLabel(
            pkg.analysis_overview?.final_check_label,
          );
          const isLevelCustomized =
            pkg.analysis_overview?.inhibitors?.customized ?? false;

          return {
            id: pkg.package_id,
            label: (
              <Link
                to={`${projectsRoute}/${PROJECT_ROUTE}/${project.project_id}/${PACKAGE_ROUTE}/${pkg.package_id}`}
                title={pkg.name}
                className={styles["package-label"]}
              >
                <CheckLabel
                  status={status}
                  scanLevel={scanLevel}
                  isLevelCustomized={isLevelCustomized}
                  isReproducibilityCheck={isRepro}
                  isDiffCheck={isDiff}
                />
                <HighlightMatch value={pkg.name} regex={filterValue} />
              </Link>
            ),
            // Collapsed rail: drop the status glyph and spend the narrow width
            // on the name, which distinguishes packages far better than a
            // pass/fail glyph.
            shortLabel: (
              <Link
                to={`${projectsRoute}/${PROJECT_ROUTE}/${project.project_id}/${PACKAGE_ROUTE}/${pkg.package_id}`}
                title={pkg.name}
              >
                {pkg.name}
              </Link>
            ),
            menu: (
              <AllowedTo permissions={[PERMISSION.CREATE_PACKAGE]}>
                <PackageMenu
                  id={`package-menu-${pkg.package_id}`}
                  packageInfo={pkg}
                  onDelete={(packageInfo) => {
                    setSelectedPackageInfo(packageInfo);
                    openDeleteDialog();
                  }}
                  onEdit={(packageInfo) =>
                    handleOpenEditDialog(
                      project.project_id,
                      project.name,
                      packageInfo,
                    )
                  }
                  onConfigureImport={(packageInfo) => {
                    setSelectedPackageInfo(packageInfo);
                    configureImportDialog.open();
                  }}
                  onDetach={(packageInfo) => {
                    setSelectedPackageInfo(packageInfo);
                    detachDialog.open();
                  }}
                />
              </AllowedTo>
            ),
          };
        });

        if (canCrudPackage) {
          const allowCreatePackage = project.packages.length < maxPackagesLimit;

          packageNodes.push({
            id: `create-package-${project.project_id}`,
            label: (
              <Button
                color="secondary"
                isInline
                isFullWidth
                disabled={!allowCreatePackage}
                className={styles["create-button"]}
                onClick={() =>
                  handleOpenEditDialog(project.project_id, project.name)
                }
              >
                <AddIcon size="xs" />
              </Button>
            ),
          });
        }

        return {
          id: project.project_id,
          label: (
            <Link
              to={`${projectsRoute}/${PROJECT_ROUTE}/${project.project_id}`}
              title={project.name}
            >
              <HighlightMatch value={project.name} regex={filterValue} />
            </Link>
          ),
          menu: (
            <ProjectMenu
              id={`${PROJECT_MENU}-${project.project_id}`}
              project={project}
              maxPackagesLimit={maxPackagesLimit}
            />
          ),
          children: packageNodes,
        };
      }),
    [
      projects,
      projectsRoute,
      filterValue,
      canCrudPackage,
      maxPackagesLimit,
      configureImportDialog,
      detachDialog,
      handleOpenEditDialog,
      openDeleteDialog,
    ],
  );

  return (
    <nav
      aria-label="projects-navigation"
      className={styles["projects-navigation"]}
      id={PROJECTS_NAVIGATION}
    >
      <TreeView
        virtualized
        scrollToSelected
        aria-label="projects-navigation"
        selected={selected}
        nodes={nodes}
        empty={{
          subject: "projects",
          filters: {
            active: Boolean(filterValue),
            onReset: onResetSearch,
          },
        }}
        title="Projects"
        style={{ height: "100%" }}
      />
      {selectedProject && (
        <CreatePackage
          isOpen={isEditDialogOpen}
          onClose={closeEditDialog}
          packageInfo={selectedPackageInfo}
          projectId={selectedProject.id}
          projectName={selectedProject.name}
        />
      )}
      {selectedPackageInfo && (
        <DeletePackageDialog
          packageDataOrId={selectedPackageInfo.package_id}
          projectName={
            projects.find(
              (project) =>
                project.project_id === selectedPackageInfo.project_id,
            )?.name ?? ""
          }
          isOpen={isDeleteDialogOpen}
          onClose={closeDeleteDialog}
        />
      )}
      {selectedPackageInfo?.external_catalog && (
        <>
          <ConfigureImport
            isOpen={configureImportDialog.isOpen}
            packageId={selectedPackageInfo.package_id}
            packageName={selectedPackageInfo.name}
            externalPackage={selectedPackageInfo.external_catalog}
            onClose={configureImportDialog.close}
          />
          <DetachFromCatalog
            isOpen={detachDialog.isOpen}
            packageId={selectedPackageInfo.package_id}
            packageName={selectedPackageInfo.name}
            catalogName={
              selectedPackageInfo.external_catalog.catalog_display_name
            }
            onClose={detachDialog.close}
          />
        </>
      )}
    </nav>
  );
};

export default NavigationSidebar;
